import React, { useRef, useState } from "react";
import usePrefersReducedMotion from "../../../../../shared/hooks/usePrefersReducedMotion";
import useSlidingIndicator from "../../../../../shared/hooks/useSlidingIndicator";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaCopyButton from "../../../../ui-kit/kuika-copy-button/kuika-copy-button";
import KuikaSwap, { KuikaSwapDirection, KuikaSwapVariant } from "../../../../ui-kit/kuika-swap/kuika-swap";
import KuikaTabs from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";

// ── Token inventory (values are read live from the stylesheet, never repeated here) ────────────

interface IMotionToken {
  token: string;
  usage: string;
}

interface ITokenGroup {
  label: string;
  caption: string;
  tokens: IMotionToken[];
}

const TOKEN_GROUPS: ITokenGroup[] = [
  {
    label: "Durations",
    caption: "--dur-* · --delay-*",
    tokens: [
      { token: "--dur-press", usage: "press feedback" },
      { token: "--dur-hover", usage: "hover background" },
      { token: "--dur-fast", usage: "tooltip, popover; modal exit" },
      { token: "--dur-base", usage: "dropdown, select, accordion, content swap" },
      { token: "--dur-layout", usage: "sliding indicators — pair with --spring-layout" },
      { token: "--dur-panel", usage: "modal enter" },
      { token: "--dur-drawer", usage: "drawer enter" },
      { token: "--dur-drawer-exit", usage: "drawer exit" },
      { token: "--delay-drawer-content", usage: "drawer content, after the sheet" },
      { token: "--dur-exit-factor", usage: "exit = enter × factor" },
      { token: "--dur-shake", usage: "kuika-shake" },
      { token: "--dur-shake-sm", usage: "kuika-shake-sm" },
      { token: "--dur-pulse", usage: "kuika-pulse, one breath" }
    ]
  },
  {
    label: "Curves",
    caption: "--ease-* · --spring-*",
    tokens: [
      { token: "--ease-out", usage: "enter / exit" },
      { token: "--ease-in-out", usage: "an on-screen object changing place" },
      { token: "--ease-drawer", usage: "drawer / sheet" },
      { token: "--ease-exit", usage: "exit" },
      { token: "--spring-press", usage: "press — write var(--spring-press, var(--ease-out))" },
      { token: "--spring-layout", usage: "indicators — write var(--spring-layout, var(--ease-out))" }
    ]
  },
  {
    label: "Reduced motion",
    caption: "multipliers — 0 under prefers-reduced-motion",
    tokens: [
      { token: "--motion-travel", usage: "every distance" },
      { token: "--motion-scale", usage: "every scale delta" },
      { token: "--motion-blur", usage: "every motion blur" },
      { token: "--overlay-blur", usage: "modal / drawer backdrop — stays under reduced motion" }
    ]
  }
];

const ALL_TOKENS: string[] = TOKEN_GROUPS.flatMap((group: ITokenGroup): string[] =>
  group.tokens.map((entry: IMotionToken): string => entry.token)
);

const MAX_VALUE_LENGTH: number = 44;

// ── Curve race ───────────────────────────────────────────────────────────────────────────────

interface IRaceLane {
  token: string;
  curve: string;
}

const RACE_LANES: IRaceLane[] = [
  { token: "--ease-out", curve: "var(--ease-out)" },
  { token: "--ease-in-out", curve: "var(--ease-in-out)" },
  { token: "--ease-drawer", curve: "var(--ease-drawer)" },
  { token: "--spring-press", curve: "var(--spring-press, var(--ease-out))" },
  { token: "--spring-layout", curve: "var(--spring-layout, var(--ease-out))" }
];

const RACE_DOT_SIZE: number = 12;

// ── Keyframes ────────────────────────────────────────────────────────────────────────────────

interface IKeyframeDemo {
  name: string;
  animation: string;
  note: string;
}

const KEYFRAME_DEMOS: IKeyframeDemo[] = [
  { name: "kuika-shake", animation: "kuika-shake var(--dur-shake) linear", note: "invalid field on submit" },
  { name: "kuika-shake-sm", animation: "kuika-shake-sm var(--dur-shake-sm) linear", note: "aria-disabled press" },
  { name: "kuika-row-in", animation: "kuika-row-in var(--dur-base) var(--ease-out) both", note: "row arriving" },
  { name: "kuika-pane-in", animation: "kuika-pane-in var(--dur-base) var(--ease-out) both", note: "tab content" },
  { name: "kuika-pulse", animation: "kuika-pulse var(--dur-pulse) var(--ease-in-out) infinite", note: "waiting loop" }
];

const DRAW_ANIMATION: string = "kuika-draw var(--dur-base) var(--ease-out) both";

// ── Swap ─────────────────────────────────────────────────────────────────────────────────────

const SAVE_LABELS: string[] = ["Save changes", "Saving…", "Saved"];

// ── Sliding indicator ────────────────────────────────────────────────────────────────────────

const RAIL_ITEMS: string[] = ["Screens", "Datasources", "Actions", "Workflows"];

// ── Copyable CSS ─────────────────────────────────────────────────────────────────────────────

const USAGE_SNIPPET: string = `/* Enter / exit */
.my-popover { animation: kuika-swap-in var(--dur-fast) var(--ease-out) both; }

/* An on-screen object changing place */
.my-thumb { transition: translate var(--dur-layout) var(--spring-layout, var(--ease-out)); }

/* Refusal */
.my-field--invalid { animation: kuika-shake var(--dur-shake) linear; }

/* Every distance goes through the reduced-motion multiplier */
.my-row { translate: 0 calc(4px * var(--motion-travel)); }`;

// ── Shared inline styles ─────────────────────────────────────────────────────────────────────

interface IMotionScopeStyle extends React.CSSProperties {
  "--motion-travel"?: number;
  "--motion-scale"?: number;
  "--motion-blur"?: number;
}

// `display: contents` keeps the four sections direct flex children of the frame body (its gap
// still spaces them) while the multipliers still inherit through the element tree.
const FOLLOW_SYSTEM_SCOPE_STYLE: IMotionScopeStyle = { display: "contents" };

const REDUCED_SCOPE_STYLE: IMotionScopeStyle = {
  display: "contents",
  "--motion-travel": 0,
  "--motion-scale": 0,
  "--motion-blur": 0
};

const MONO_TEXT: React.CSSProperties = {
  fontFamily: "var(--lab-mono)",
  fontSize: 11,
  color: "var(--lab-ink)"
};

const MUTED_TEXT: React.CSSProperties = {
  fontSize: 11,
  color: "var(--lab-sublabel)"
};

const DEMO_SURFACE: React.CSSProperties = {
  display: "grid",
  placeItems: "center",
  minHeight: 64,
  padding: 12,
  background: "var(--lab-paper)",
  border: "1px solid var(--lab-border)"
};

const truncate = (value: string): string =>
  value.length > MAX_VALUE_LENGTH ? `${value.slice(0, MAX_VALUE_LENGTH)}…` : value;

const readTokenValues = (): Record<string, string> => {
  const computed: CSSStyleDeclaration = getComputedStyle(document.documentElement);
  const values: Record<string, string> = {};

  ALL_TOKENS.forEach((token: string): void => {
    values[token] = computed.getPropertyValue(token).trim().replace(/\s+/g, " ");
  });

  return values;
};

// ── Sections ─────────────────────────────────────────────────────────────────────────────────

const TokenTable = (): JSX.Element => {
  // Subscribed only to re-render when the OS setting flips — the multipliers change with it.
  usePrefersReducedMotion();
  const values: Record<string, string> = readTokenValues();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, width: "100%" }}>
      {TOKEN_GROUPS.map((group: ITokenGroup): JSX.Element => (
        <div key={group.label} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span className="lab-caption">
            {group.label} — {group.caption}
          </span>
          {group.tokens.map((entry: IMotionToken): JSX.Element => (
            <div
              key={entry.token}
              style={{ display: "grid", gridTemplateColumns: "180px 260px 1fr", gap: 16, alignItems: "baseline" }}
            >
              <span style={MONO_TEXT}>{entry.token}</span>
              <span style={{ ...MONO_TEXT, color: "var(--lab-ink-muted)" }} title={values[entry.token]}>
                {truncate(values[entry.token] ?? "")}
              </span>
              <span style={MUTED_TEXT}>{entry.usage}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const CurveRace = (): JSX.Element => {
  const [isAtEnd, setIsAtEnd] = useState<boolean>(false);
  const [speed, setSpeed] = useState<string>("1");
  const durationFactor: number = speed === "1" ? 1 : 4;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
      <div className="lab-row">
        <KuikaButton type="primary" size="sm" onClick={(): void => setIsAtEnd(!isAtEnd)}>
          {isAtEnd ? "Race back" : "Race"}
        </KuikaButton>
        <KuikaTabs
          size="sm"
          value={speed}
          onChange={setSpeed}
          options={[
            { value: "1", label: "--dur-layout" },
            { value: "4", label: "× 4 slow" }
          ]}
        />
      </div>
      {RACE_LANES.map((lane: IRaceLane): JSX.Element => (
        <div
          key={lane.token}
          style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 16, alignItems: "center" }}
        >
          <span style={MONO_TEXT}>{lane.token}</span>
          <div style={{ position: "relative", height: 20, borderBottom: "1px solid var(--lab-border)" }}>
            <div
              style={{
                width: `calc(100% - ${RACE_DOT_SIZE}px)`,
                height: "100%",
                display: "flex",
                alignItems: "center",
                // Travel goes through the multiplier like any other: under reduced motion the dots stay.
                translate: isAtEnd ? "calc(100% * var(--motion-travel)) 0" : "0 0",
                transition: `translate calc(var(--dur-layout) * ${durationFactor}) ${lane.curve}`
              }}
            >
              <span
                style={{
                  width: RACE_DOT_SIZE,
                  height: RACE_DOT_SIZE,
                  borderRadius: "var(--radius-pill)",
                  background: "var(--lab-accent)"
                }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const KeyframeGallery = (): JSX.Element => {
  const [plays, setPlays] = useState<number>(0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
      <div className="lab-row">
        <KuikaButton type="secondary" size="sm" onClick={(): void => setPlays(plays + 1)}>
          Replay all
        </KuikaButton>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
        {KEYFRAME_DEMOS.map((demo: IKeyframeDemo): JSX.Element => (
          <div key={demo.name} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={DEMO_SURFACE}>
              <span
                key={plays}
                style={{
                  display: "inline-block",
                  padding: "4px 12px",
                  border: "1px solid var(--lab-border-strong)",
                  background: "var(--lab-paper-alt)",
                  animation: demo.animation,
                  ...MONO_TEXT
                }}
              >
                {demo.name}
              </span>
            </div>
            <span style={MUTED_TEXT}>{demo.note}</span>
          </div>
        ))}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={DEMO_SURFACE}>
            {/* The subject of kuika-draw is a stroke, so this one path is drawn inline on purpose. */}
            <svg key={plays} width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M4 12.5l5 5L20 6.5"
                pathLength={24}
                stroke="var(--lab-accent)"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ strokeDasharray: 24, animation: DRAW_ANIMATION }}
              />
            </svg>
          </div>
          <span style={MUTED_TEXT}>kuika-draw — checkmark</span>
        </div>
      </div>
    </div>
  );
};

const SwapDemo = (): JSX.Element => {
  const [labelIndex, setLabelIndex] = useState<number>(0);
  const [count, setCount] = useState<number>(12);
  const [countDirection, setCountDirection] = useState<KuikaSwapDirection>("up");
  const [variant, setVariant] = useState<string>("roll");
  const swapVariant: KuikaSwapVariant = variant === "blur" ? "blur" : "roll";

  const changeCount = (delta: number): void => {
    setCountDirection(delta > 0 ? "up" : "down");
    setCount(count + delta);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
      <KuikaTabs
        size="sm"
        value={variant}
        onChange={setVariant}
        options={[
          { value: "roll", label: "roll" },
          { value: "blur", label: "blur" }
        ]}
      />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={DEMO_SURFACE}>
            <span style={{ ...MONO_TEXT, fontSize: 13 }}>
              <KuikaSwap contentKey={labelIndex} variant={swapVariant}>
                {SAVE_LABELS[labelIndex]}
              </KuikaSwap>
            </span>
          </div>
          <KuikaButton
            type="secondary"
            size="sm"
            onClick={(): void => setLabelIndex((labelIndex + 1) % SAVE_LABELS.length)}
          >
            Next state
          </KuikaButton>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={DEMO_SURFACE}>
            <span style={{ ...MONO_TEXT, fontSize: 20, fontVariantNumeric: "tabular-nums" }}>
              <KuikaSwap contentKey={count} variant={swapVariant} direction={countDirection}>
                {count}
              </KuikaSwap>
            </span>
          </div>
          <div className="lab-row">
            <KuikaButton type="secondary" size="sm" onClick={(): void => changeCount(-1)}>
              − 1
            </KuikaButton>
            <KuikaButton type="secondary" size="sm" onClick={(): void => changeCount(1)}>
              + 1
            </KuikaButton>
          </div>
        </div>
      </div>
    </div>
  );
};

const SlidingIndicatorDemo = (): JSX.Element => {
  const railRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const [activeItem, setActiveItem] = useState<string>(RAIL_ITEMS[0]);
  const [isHidden, setIsHidden] = useState<boolean>(false);

  useSlidingIndicator(railRef, activeItem, { hidden: isHidden });

  return (
    <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
      <div
        ref={railRef}
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: 200,
          padding: 4,
          background: "var(--lab-paper)",
          border: "1px solid var(--lab-border)"
        }}
      >
        <span
          className="kuika-sliding-indicator"
          style={{ background: "var(--lab-accent-soft)", borderRadius: "var(--radius-xs)" }}
        />
        {RAIL_ITEMS.map((item: string): JSX.Element => (
          <button
            key={item}
            type="button"
            data-indicator-key={item}
            aria-pressed={item === activeItem}
            onClick={(): void => setActiveItem(item)}
            style={{
              position: "relative",
              height: 32,
              padding: "0 12px",
              border: "none",
              background: "transparent",
              textAlign: "left",
              cursor: "pointer",
              color: item === activeItem ? "var(--lab-ink)" : "var(--lab-ink-muted)",
              fontSize: 13
            }}
          >
            {item}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <KuikaButton type="secondary" size="sm" onClick={(): void => setIsHidden(!isHidden)}>
          {isHidden ? "Show indicator" : "Hide (drag)"}
        </KuikaButton>
        <span style={{ ...MUTED_TEXT, maxWidth: 280 }}>
          The first placement never slides; resizes snap; reduced motion is instant. The container is position: relative
          and the indicator is its direct child.
        </span>
      </div>
    </div>
  );
};

const CopyableCss = (): JSX.Element => {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
      <pre
        style={{
          margin: 0,
          padding: 12,
          overflowX: "auto",
          background: "var(--lab-paper-alt)",
          border: "1px solid var(--lab-border)",
          ...MONO_TEXT
        }}
      >
        {USAGE_SNIPPET}
      </pre>
      <div className="lab-row">
        {/* The shared copy control: its label rolls to "Copied" only when the clipboard took the text. */}
        <KuikaCopyButton variant="text" type="secondary" size="sm" value={USAGE_SNIPPET} label="Copy CSS" />
      </div>
    </div>
  );
};

// ── Story ────────────────────────────────────────────────────────────────────────────────────

export const MotionTokensStory: React.FC = (): JSX.Element => {
  const prefersReducedMotion: boolean = usePrefersReducedMotion();
  const [previewMode, setPreviewMode] = useState<string>("system");
  const scopeStyle: IMotionScopeStyle = previewMode === "reduced" ? REDUCED_SCOPE_STYLE : FOLLOW_SYSTEM_SCOPE_STYLE;

  return (
    <ShowcaseFrame
      title="Motion Tokens"
      componentName="motion.scss · KuikaSwap · useSlidingIndicator"
      sourcePath="styles/base/motion.scss"
      description="Durations, curves, shared keyframes and the reduced-motion multipliers. Components never write a literal duration, curve or distance — only these names."
    >
      <SpecRow
        label="Reduced motion preview"
        caption={`OS setting: ${prefersReducedMotion ? "reduce" : "no preference"} · preview zeroes the CSS multipliers below`}
        align="start"
      >
        <KuikaTabs
          size="sm"
          value={previewMode}
          onChange={setPreviewMode}
          options={[
            { value: "system", label: "Follow system" },
            { value: "reduced", label: "Preview reduced" }
          ]}
        />
      </SpecRow>
      <div style={scopeStyle}>
        <SpecRow label="Curve race" caption="same duration, five curves · travel × --motion-travel" align="start">
          <CurveRace />
        </SpecRow>
        <SpecRow label="Keyframes" caption="every distance × --motion-travel" align="start">
          <KeyframeGallery />
        </SpecRow>
        <SpecRow label="KuikaSwap" caption="content change in one grid cell" align="start">
          <SwapDemo />
        </SpecRow>
        <SpecRow label="useSlidingIndicator" caption=".kuika-sliding-indicator + data-indicator-key" align="start">
          <SlidingIndicatorDemo />
        </SpecRow>
      </div>
      <SpecRow label="Tokens" caption="values read live from :root" align="start">
        <TokenTable />
      </SpecRow>
      <SpecRow label="CSS" caption="copy and adapt" align="start">
        <CopyableCss />
      </SpecRow>
    </ShowcaseFrame>
  );
};
